/**
 * Hybrid Intelligence Router
 *
 * Routes intelligence requests to appropriate source (API or local) based on
 * user context and risk assessment. Prioritizes accuracy for crisis situations
 * and speed/cost for routine operations.
 */

import { CrisisDetector } from './CrisisDetector.js';
import { LocalIntelligence } from './LocalIntelligence.js';
// Removed ClaudeAPIService - routing directly through API endpoint
import { IntelligenceCache } from '../cache/IntelligenceCache.js';
import { CostOptimizer } from '../api/CostOptimizer.js';
import { UserQuotaManager } from '../api/UserQuotaManager.js';

class HybridIntelligenceRouter {
  constructor() {
    this.crisisDetector = new CrisisDetector();
    this.localIntelligence = new LocalIntelligence();
    this.cache = new IntelligenceCache();
    this.costOptimizer = new CostOptimizer();
    this.quotaManager = new UserQuotaManager();
  }

  /**
   * Main routing method - determines whether to use API or local intelligence
   * @param {Array} tasks - Current timeline tasks
   * @param {Object} userContext - User context including preferences and state
   * @returns {Object} Intelligence response with suggestions and executable actions
   */
  async generateSuggestions(tasks, userContext) {
    try {
      // Assess user risk level
      const riskAssessment = await this.assessUserRisk(userContext, tasks);

      // Log routing decision for monitoring
      console.log('[Intelligence Router] Risk assessment:', riskAssessment);

      // Route based on risk level
      if (riskAssessment.level === 'crisis' || riskAssessment.level === 'critical') {
        return await this.handleCrisisWithAPI(tasks, userContext, riskAssessment);
      } else if (riskAssessment.level === 'high_stress') {
        return await this.handleHighStressWithAPI(tasks, userContext, riskAssessment);
      } else if (this.shouldUseAPIForComplexity(tasks, userContext)) {
        return await this.handleComplexWithAPI(tasks, userContext);
      } else {
        return await this.handleWithLocal(tasks, userContext);
      }
    } catch (error) {
      console.error('[Intelligence Router] Error generating suggestions:', error);
      return await this.getFallbackSuggestions(tasks, userContext, error);
    }
  }

  /**
   * Assess user risk level to determine routing
   */
  async assessUserRisk(context, tasks) {
    const assessment = await this.crisisDetector.assessCrisisLevel(
      context.userId,
      tasks
    );

    // Add quick local calculations for immediate routing
    const signals = {
      overdueCount: tasks.filter(t => t.isOverdue && !t.completed).length,
      criticalOverdueCount: tasks.filter(t =>
        t.isOverdue && !t.completed && t.priority === 'critical'
      ).length,
      deadlineProximity: this.getClosestDeadline(tasks),
      recentCompletionRate: context.completionRate7Days || 1.0,
      taskChurn: this.calculateTaskChurn(tasks),
      cognitiveOverload: this.assessCognitiveLoad(tasks)
    };

    // Conservative thresholds - err on side of getting help
    let level = assessment.level;

    // Override with crisis if signals are severe
    if (signals.overdueCount > 10 ||
        signals.criticalOverdueCount > 3 ||
        signals.deadlineProximity < 48 ||
        signals.recentCompletionRate < 0.3) {
      level = 'crisis';
    }

    return {
      level,
      score: assessment.score,
      signals,
      factors: assessment.factors,
      confidence: assessment.confidence || 0.8
    };
  }

  /**
   * Handle crisis situations with API intelligence
   */
  async handleCrisisWithAPI(tasks, userContext, riskAssessment) {
    console.log('[Intelligence Router] Crisis detected - using API for recovery plan');

    // Check cache first
    const cacheKey = this.generateCacheKey('crisis', tasks, userContext);
    const cached = await this.cache.get(cacheKey);

    if (cached.hit) {
      console.log('[Intelligence Router] Using cached crisis plan');
      return {
        ...cached.response,
        source: 'api_cached',
        cacheInfo: {
          type: cached.type,
          similarity: cached.similarity
        }
      };
    }

    // Determine which model to use based on severity
    let modelChoice = riskAssessment.level === 'critical' ? 'sonnet' : 'sonnet';

    // Check user quota for selected model
    const quotaCheck = await this.quotaManager.checkQuota(
      userContext.userId,
      modelChoice,
      {
        overdueCount: riskAssessment.signals?.overdueCount || 0,
        criticalOverdue: riskAssessment.signals?.criticalOverdueCount || 0,
        hoursUntilDeadline: riskAssessment.signals?.deadlineProximity || 999
      }
    );

    // Handle quota exceeded
    if (!quotaCheck.allowed) {
      console.warn(`[Intelligence Router] ${modelChoice} quota exceeded for user ${userContext.userId}`);

      // Try fallback model if suggested
      if (quotaCheck.suggestion?.fallbackModel && quotaCheck.suggestion.fallbackModel !== 'wait') {
        console.log(`[Intelligence Router] Falling back to ${quotaCheck.suggestion.fallbackModel}`);

        // Return message explaining the downgrade
        const fallbackMessage = {
          quotaExceeded: true,
          originalModel: modelChoice,
          usingModel: quotaCheck.suggestion.fallbackModel,
          message: quotaCheck.suggestion.message,
          quality: quotaCheck.suggestion.quality
        };

        // For Haiku fallback, use local intelligence
        if (quotaCheck.suggestion.fallbackModel === 'haiku') {
          return await this.handleWithLocal(tasks, userContext, fallbackMessage);
        }

        // Otherwise use the fallback model
        // Update model choice and continue
        modelChoice = quotaCheck.suggestion.fallbackModel;
      } else {
        // No fallback available or should wait
        return {
          source: 'quota_exceeded',
          allowed: false,
          message: quotaCheck.suggestion?.message || 'Daily API limit reached. Please try again tomorrow.',
          resetIn: quotaCheck.resetIn,
          suggestion: 'Consider seeking help from a counselor or advisor',
          fallbackPlan: await this.localIntelligence.generateConservativeSuggestions(tasks, userContext)
        };
      }
    }

    // Check budget (legacy check, can be removed if using quota system)
    const budgetCheck = await this.costOptimizer.checkBudget(
      userContext.userId,
      'crisis'
    );

    if (!budgetCheck.allowed) {
      console.warn('[Intelligence Router] Budget exceeded, falling back to local');
      return await this.handleWithLocal(tasks, userContext, {
        budgetExceeded: true,
        originalLevel: 'crisis'
      });
    }

    // Make API call for crisis recovery plan
    try {
      const apiResponse = await this.callCrisisRecoveryAPI(tasks, userContext, riskAssessment);

      // Cache the response
      await this.cache.set(cacheKey, apiResponse, {
        duration: 3600, // 1 hour for crisis plans
        tags: ['crisis', userContext.userId]
      });

      // Track quota usage
      await this.quotaManager.recordUsage(userContext.userId, modelChoice, {
        type: 'crisis_recovery',
        taskCount: tasks.length,
        overdueCount: riskAssessment.signals?.overdueCount || 0
      });

      // Track cost (legacy)
      await this.costOptimizer.trackUsage(
        userContext.userId,
        apiResponse.cost || 2.0,
        'crisis_recovery'
      );

      // Get remaining quota for user info
      const quotaStatus = await this.quotaManager.getQuotaStatus(userContext.userId);

      return {
        ...apiResponse,
        source: 'api',
        model: modelChoice,
        confidence: 0.95,
        riskLevel: riskAssessment.level,
        executionReady: true,
        quotaInfo: {
          modelUsed: modelChoice,
          remaining: quotaStatus[modelChoice].daily.remaining,
          nextReset: this.quotaManager.getResetTime(userContext.userId, modelChoice)
        }
      };
    } catch (error) {
      console.error('[Intelligence Router] API call failed:', error);
      return await this.getFallbackSuggestions(tasks, userContext, error);
    }
  }

  /**
   * Handle high stress situations with API
   */
  async handleHighStressWithAPI(tasks, userContext, riskAssessment) {
    console.log('[Intelligence Router] High stress detected - using API for optimization');

    // Similar to crisis but with different prompting
    const cacheKey = this.generateCacheKey('high_stress', tasks, userContext);
    const cached = await this.cache.get(cacheKey);

    if (cached.hit) {
      return {
        ...cached.response,
        source: 'api_cached',
        cacheInfo: cached
      };
    }

    try {
      const apiResponse = await this.callWorkloadOptimizationAPI(tasks, userContext, riskAssessment);

      await this.cache.set(cacheKey, apiResponse, {
        duration: 10800, // 3 hours for high stress
        tags: ['high_stress', userContext.userId]
      });

      return {
        ...apiResponse,
        source: 'api',
        confidence: 0.9,
        riskLevel: riskAssessment.level,
        executionReady: true
      };
    } catch (error) {
      return await this.getFallbackSuggestions(tasks, userContext, error);
    }
  }

  /**
   * Handle complex scenarios with API
   */
  async handleComplexWithAPI(tasks, userContext) {
    console.log('[Intelligence Router] Complex scenario - using API');

    const complexity = this.assessComplexity(tasks, userContext);

    try {
      const apiResponse = await this.callSmartOptimizationAPI(tasks, userContext, complexity);

      return {
        ...apiResponse,
        source: 'api',
        confidence: 0.85,
        executionReady: complexity.type === 'essay_strategy'
      };
    } catch (error) {
      console.error('[Intelligence Router] API error, falling back to local:', error.message);
      return await this.handleWithLocal(tasks, userContext);
    }
  }

  /**
   * Handle routine scenarios with local intelligence
   */
  async handleWithLocal(tasks, userContext, metadata = {}) {
    console.log('[Intelligence Router] Using local intelligence');

    const localSuggestions = await this.localIntelligence.generateSuggestions(
      tasks,
      userContext
    );

    return {
      ...localSuggestions,
      source: 'local',
      confidence: 0.7,
      metadata,
      executionReady: false, // Local suggestions need validation
      upgradeAvailable: metadata.budgetExceeded || metadata.originalLevel === 'crisis'
    };
  }

  /**
   * Determine if complexity warrants API usage
   */
  shouldUseAPIForComplexity(tasks, context) {
    // Complex essay scenarios
    if (this.hasComplexEssays(tasks, context)) {
      return true;
    }

    // Multiple deadline conflicts
    if (this.hasDeadlineConflicts(tasks) > 3) {
      return true;
    }

    // High task churn (lots of rescheduling)
    if (this.calculateTaskChurn(tasks) > 5) {
      return true;
    }

    // User explicitly requested help
    if (context.userRequestedHelp) {
      return true;
    }

    return false;
  }

  /**
   * Check for complex essay scenarios
   */
  hasComplexEssays(tasks, _context) {
    const essays = tasks.filter(t =>
      t.type === 'essay' ||
      t.category === 'essay' ||
      t.title?.toLowerCase().includes('essay')
    );

    // 8+ essays need strategic planning
    if (essays.length >= 8) {
      return true;
    }

    // Multiple colleges with overlapping themes
    const colleges = [...new Set(essays.map(e => e.college_id))];
    if (colleges.length >= 5 && essays.length >= 5) {
      return true;
    }

    return false;
  }

  /**
   * Count deadline conflicts
   */
  hasDeadlineConflicts(tasks) {
    const deadlines = {};

    tasks.forEach(task => {
      if (task.deadline && !task.completed) {
        const dateKey = new Date(task.deadline).toDateString();
        deadlines[dateKey] = (deadlines[dateKey] || 0) + 1;
      }
    });

    return Object.values(deadlines).filter(count => count >= 3).length;
  }

  /**
   * Calculate task churn (rescheduling frequency)
   */
  calculateTaskChurn(tasks) {
    return tasks.filter(t =>
      t.rescheduleCount && t.rescheduleCount > 2
    ).length;
  }

  /**
   * Assess cognitive load
   */
  assessCognitiveLoad(tasks) {
    const today = new Date();
    const todaysTasks = tasks.filter(t => {
      const taskDate = new Date(t.scheduled_date || t.deadline);
      return taskDate.toDateString() === today.toDateString() && !t.completed;
    });

    const totalCognitiveLoad = todaysTasks.reduce((sum, task) => {
      const load = this.getTaskCognitiveLoad(task);
      return sum + load;
    }, 0);

    // Normalize to 0-1 scale (assuming 10 is max daily cognitive capacity)
    return Math.min(1, totalCognitiveLoad / 10);
  }

  /**
   * Get cognitive load for a single task
   */
  getTaskCognitiveLoad(task) {
    const loadMap = {
      'essay_writing': 3,
      'essay_revision': 2,
      'research': 2,
      'application': 1.5,
      'admin': 0.5,
      'default': 1
    };

    return loadMap[task.type] || loadMap.default;
  }

  /**
   * Get closest deadline in hours
   */
  getClosestDeadline(tasks) {
    const now = new Date();
    let closestHours = Infinity;

    tasks.forEach(task => {
      if (task.deadline && !task.completed) {
        const deadline = new Date(task.deadline);
        const hoursUntil = (deadline - now) / (1000 * 60 * 60);
        if (hoursUntil > 0 && hoursUntil < closestHours) {
          closestHours = hoursUntil;
        }
      }
    });

    return closestHours === Infinity ? 999 : closestHours;
  }

  /**
   * Assess complexity of the scenario
   */
  assessComplexity(tasks, _context) {
    const essays = tasks.filter(t => t.type === 'essay').length;
    const conflicts = this.hasDeadlineConflicts(tasks);
    const churn = this.calculateTaskChurn(tasks);

    if (essays >= 8) {
      return { type: 'essay_strategy', level: 'high' };
    }
    if (conflicts > 3) {
      return { type: 'deadline_management', level: 'high' };
    }
    if (churn > 5) {
      return { type: 'schedule_stabilization', level: 'medium' };
    }

    return { type: 'general_optimization', level: 'low' };
  }

  /**
   * Generate cache key for request
   */
  generateCacheKey(type, tasks, context) {
    const key = {
      type,
      overdueCount: tasks.filter(t => t.isOverdue).length,
      taskCount: tasks.length,
      userId: context.userId,
      date: new Date().toDateString()
    };

    return JSON.stringify(key);
  }

  /**
   * Provide fallback suggestions when API fails
   */
  async getFallbackSuggestions(tasks, userContext, error) {
    console.warn('[Intelligence Router] Providing fallback suggestions due to error:', error);

    // Try to use cached response from similar situation
    const similarCached = await this.cache.findSimilar({
      tasks,
      userContext,
      minSimilarity: 0.7
    });

    if (similarCached) {
      return {
        ...similarCached,
        source: 'cached_fallback',
        warning: 'Using similar cached guidance due to temporary issue',
        confidence: similarCached.confidence * 0.8
      };
    }

    // Provide conservative local suggestions
    const localSuggestions = await this.localIntelligence.generateConservativeSuggestions(
      tasks,
      userContext
    );

    return {
      ...localSuggestions,
      source: 'local_fallback',
      warning: 'Simplified suggestions due to temporary limitations. For comprehensive help, please try again in a few minutes.',
      confidence: 0.5,
      executionReady: false
    };
  }

  /**
   * Direct API call for crisis recovery
   */
  async callCrisisRecoveryAPI(tasks, userContext, riskAssessment) {
    const overdueTasks = tasks.filter(t => t.isOverdue && !t.completed);
    // Count upcoming tasks for context (currently unused but may be useful for future features)
    const _upcomingTasks = tasks.filter(t => !t.isOverdue && !t.completed);
    const criticalTasks = tasks.filter(t => t.priority === 'critical' && !t.completed);

    const prompt = `CRISIS RECOVERY PLAN REQUEST

SITUATION:
- Total Tasks: ${tasks.length}
- Overdue: ${overdueTasks.length}
- Critical: ${criticalTasks.length}
- Risk Level: ${riskAssessment.level}
- User State: ${userContext.currentStressLevel || 'high'}

COLLEGES (if available):
${userContext.colleges ? userContext.colleges.map(c =>
  `- ${c.name}: ${c.category || 'Unknown'} school (Application round: ${c.application_round || 'Not set'})`
).join('\n') : 'No college information available'}

TASKS:
${tasks.map(t => `- ${t.title} (Due: ${t.deadline || t.scheduled_date}, Priority: ${t.priority || 'normal'}, College: ${t.college_name || 'General'}, Status: ${t.completed ? 'Done' : t.isOverdue ? 'OVERDUE' : 'Pending'})`).join('\n')}

Apply the SMART APPLICATION STRATEGY:
1. Set Reach/Target schools to Early Action by default
2. Set Safety schools to Regular Decision by default
3. Keep research tasks for EA schools (needed for Nov deadlines)
4. Defer research tasks for RD schools until December
5. Help student select ED school from reach/target options

Please create an executable recovery plan with specific actions to reduce overwhelm and get the student back on track.`;

    const systemPrompt = `You are a college application crisis counselor helping overwhelmed students recover.

CRITICAL PRINCIPLES:
1. Student mental health comes first - be realistic not aspirational
2. Better to do fewer things well than many things poorly
3. Provide specific, executable actions with deadlines
4. Focus on immediate relief first, then sustainable progress

SMART APPLICATION STRATEGY:

DEFAULT APPLICATION ROUNDS BY SCHOOL TYPE:
- Reach Schools → Early Action (strategic advantage, non-binding)
- Target Schools → Early Action (good fit, early results)
- Safety Schools → Regular Decision (less time-sensitive)
- Student's #1 choice → Early Decision (binding commitment)

PRIORITY ORDER (STRICT - by actual deadlines):
1. Select ED school from reach/target schools (if student ready to commit)
2. Complete research ONLY for EA schools (reach/target schools applying EA)
3. EA applications (typically Nov 1-15) - includes reach & target schools
4. DEFER all RD preparation until December (safety schools + excess schools)

When student is overwhelmed:
- Keep research tasks for EA schools (need info for Nov deadlines)
- DEFER research tasks for RD schools (can wait until December)
- Focus on ED school selection first (most critical decision)
- Then complete EA applications (strategic advantage worth preserving)
- Push RD work until after EA/ED outcomes in December

RESPONSE FORMAT (JSON):
{
  "explanation": {
    "summary": "Brief explanation of the recovery strategy",
    "reasoning": "Why this approach will help",
    "priorities": ["Select ED school from reach/target list", "Complete EA applications by Nov 15", "Defer RD research until December"],
    "impact": "Expected outcome"
  },
  "actions": [
    {
      "type": "reschedule|batch|delete|mark_optional|focus",
      "taskId": "task_id",
      "from": "current_date",
      "to": "new_date",
      "reason": "explanation",
      "deadline_type": "ED|EA|RD|Rolling|Other"
    }
  ],
  "riskLevel": "crisis|high_stress|moderate|normal",
  "confidence": 0.8
}`;

    const response = await fetch('/api/anthropic/analysis', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        action: 'crisis-recovery',
        model: 'claude-sonnet-4-20250514',
        temperature: 0.3,
        max_tokens: 4000,
        messages: [
          { role: 'system', content: systemPrompt },
          { role: 'user', content: prompt }
        ]
      })
    });

    if (!response.ok) {
      throw new Error(`Crisis recovery API failed: ${response.status}`);
    }

    const data = await response.json();
    const responseText = data.content || data.response || data;

    try {
      // Strip markdown code blocks if present
      let cleanedResponse = responseText;

      // If response contains markdown code blocks, extract JSON content
      if (responseText.includes('```json')) {
        const jsonMatch = responseText.match(/```json\s*([\s\S]*?)```/);
        if (jsonMatch && jsonMatch[1]) {
          cleanedResponse = jsonMatch[1].trim();
        } else {
          // Fallback: try to remove markdown markers
          cleanedResponse = responseText
            .replace(/```json\s*/g, '')
            .replace(/```.*$/gm, '')
            .trim();
        }
      }

      // If response still has extra content after JSON, find the JSON object
      if (cleanedResponse.includes('}') && cleanedResponse.lastIndexOf('}') < cleanedResponse.length - 10) {
        // Find the last closing brace that completes the JSON object
        const lastBraceIndex = this.findMatchingCloseBrace(cleanedResponse);
        if (lastBraceIndex > 0) {
          cleanedResponse = cleanedResponse.substring(0, lastBraceIndex + 1);
        }
      }

      return JSON.parse(cleanedResponse);
    } catch (e) {
      console.error('Failed to parse crisis recovery response:', e);
      console.error('Raw response:', responseText);

      // Try one more fallback: extract JSON-like content
      try {
        const jsonStart = responseText.indexOf('{');
        const jsonEnd = this.findMatchingCloseBrace(responseText.substring(jsonStart)) + jsonStart;
        if (jsonStart >= 0 && jsonEnd > jsonStart) {
          const extracted = responseText.substring(jsonStart, jsonEnd + 1);
          return JSON.parse(extracted);
        }
      } catch (fallbackError) {
        console.error('Fallback parsing also failed:', fallbackError);
      }

      throw new Error('Invalid response format from crisis recovery API');
    }
  }

  /**
   * Helper function to find matching closing brace for JSON parsing
   */
  findMatchingCloseBrace(str) {
    let depth = 0;
    let inString = false;
    let escapeNext = false;

    for (let i = 0; i < str.length; i++) {
      const char = str[i];

      if (escapeNext) {
        escapeNext = false;
        continue;
      }

      if (char === '\\') {
        escapeNext = true;
        continue;
      }

      if (char === '"' && !escapeNext) {
        inString = !inString;
        continue;
      }

      if (!inString) {
        if (char === '{') {
          depth++;
        } else if (char === '}') {
          depth--;
          if (depth === 0) {
            return i;
          }
        }
      }
    }

    return -1;
  }

  /**
   * Direct API call for workload optimization
   */
  async callWorkloadOptimizationAPI(tasks, userContext, riskAssessment) {
    const prompt = `WORKLOAD OPTIMIZATION REQUEST

High stress situation detected. Please optimize this workload:

TASKS:
${tasks.map(t => `- ${t.title} (Due: ${t.deadline || t.scheduled_date}, Priority: ${t.priority || 'normal'})`).join('\n')}

USER CONTEXT:
- Stress Level: ${riskAssessment.level}
- Available Time: ${userContext.availableHours || 'unknown'} hours/week

Provide optimization suggestions to reduce stress while maintaining progress.`;

    const response = await fetch('/api/anthropic/analysis', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        action: 'crisis-recovery',
        model: 'claude-3-sonnet-20240229',
        temperature: 0.5,
        max_tokens: 2000,
        messages: [
          { role: 'system', content: 'You are a workload optimization specialist for college students. Provide practical suggestions to reduce stress while maintaining academic progress.' },
          { role: 'user', content: prompt }
        ]
      })
    });

    if (!response.ok) {
      throw new Error(`Workload optimization API failed: ${response.status}`);
    }

    const data = await response.json();
    const responseText = data.content || data.response || data;

    try {
      // Strip markdown code blocks if present
      let cleanedResponse = responseText;
      if (responseText.includes('```json')) {
        cleanedResponse = responseText
          .replace(/```json\s*/g, '')
          .replace(/```\s*$/g, '')
          .trim();
      }

      return JSON.parse(cleanedResponse);
    } catch (e) {
      console.error('Failed to parse workload optimization response:', e);
      // Return a basic structured response if parsing fails
      return {
        explanation: { summary: "Workload optimization suggestions generated" },
        actions: [],
        riskLevel: "moderate",
        confidence: 0.6
      };
    }
  }

  /**
   * Direct API call for smart optimization
   */
  async callSmartOptimizationAPI(tasks, userContext, complexity) {
    const prompt = `SMART OPTIMIZATION REQUEST

Complexity: ${complexity.level} (${complexity.type})

TASKS:
${tasks.map(t => `- ${t.title} (Due: ${t.deadline || t.scheduled_date})`).join('\n')}

Provide intelligent optimization based on the complexity level.`;

    const response = await fetch('/api/anthropic/analysis', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        action: 'crisis-recovery',
        model: complexity.level === 'high' ? 'claude-3-sonnet-20240229' : 'claude-3-haiku-20240307',
        temperature: 0.5,
        max_tokens: 1000,
        messages: [
          { role: 'system', content: 'You are a smart scheduling optimizer for college applications. Provide efficient suggestions.' },
          { role: 'user', content: prompt }
        ]
      })
    });

    if (!response.ok) {
      throw new Error(`Smart optimization API failed: ${response.status}`);
    }

    const data = await response.json();
    const responseText = data.content || data.response || data;

    try {
      // Strip markdown code blocks if present
      let cleanedResponse = responseText;
      if (responseText.includes('```json')) {
        cleanedResponse = responseText
          .replace(/```json\s*/g, '')
          .replace(/```\s*$/g, '')
          .trim();
      }

      return JSON.parse(cleanedResponse);
    } catch (e) {
      console.error('Failed to parse smart optimization response:', e);
      return {
        explanation: { summary: "Smart optimization suggestions generated" },
        actions: [],
        riskLevel: "normal",
        confidence: 0.7
      };
    }
  }
}

// Export singleton instance
export default new HybridIntelligenceRouter();